import { debugLog } from './debug'
import type { 
  CourseQueryParams, 
  Course, 
  CreateCourseInput, 
  UpdateCourseInput, 
  User, 
  UpdateUserInput,
  UserQueryParams,
  CreateUserInput,
  AdminUpdateUserInput,
  LoginResponse, 
  RefreshTokenResponse,
  CheckoutSessionResponse,
  PaymentStatusResponse,
  AdminStats,
  TeacherStats,
  AppSettings,
  ClientUiConfig,
  ApiResponse,
  Enrollment,
  EnrollmentQueryParams,
  CreateEnrollmentInput,
  UpdateEnrollmentInput,
  Combo,
  ComboQueryParams,
  CreateComboInput,
  UpdateComboInput,
  CreateUserFromTelegramInput,
  DevLoginInput,
  DevLoginResponse
} from '@/types/api'
import { mockApiClient } from './mock-api'
import { apiModeManager } from './api-mode-manager'
import { mapUserData, mapUsersData } from './user-data-mapper'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

export class ApiError extends Error {
  status: number
  retryAfterSeconds?: number

  constructor(status: number, message: string, retryAfterSeconds?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.retryAfterSeconds = retryAfterSeconds
  }
}

// 401 從任何 API 回來都代表憑證失效（過期或被撤銷）：
// 清除本地憑證並重置認證狀態，讓 AuthGuard 重新走登入流程
// （Mini App 內會自動用 initData 重新登入，體感上是無縫續期）。
// 動態 import 避免 api.ts <-> stores/auth.ts 的循環相依。
async function handleUnauthorized() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('token_expires_at')
  const { useAuthStore } = await import('@/stores/auth')
  useAuthStore.getState().resetAuth()
}

class ApiClient {
  private baseURL: string

  constructor(baseURL: string) {
    this.baseURL = baseURL
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`
    
    // Get JWT token from localStorage
    const token = typeof window !== 'undefined' 
      ? localStorage.getItem('access_token') 
      : null
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    // Add JWT Authorization header
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    // Log combo-related requests
    const isComboRequest = endpoint.includes('/combos')
    if (isComboRequest) {
      debugLog('🔌 [HTTP] Sending request:', {
        url,
        method: options.method || 'GET',
        headers: { ...headers, Authorization: token ? 'Bearer [REDACTED]' : undefined },
        body: options.body ? JSON.parse(options.body as string) : undefined,
        timestamp: new Date().toISOString()
      })
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: 'Network error',
        message: `HTTP ${response.status}`,
      }))

      if (isComboRequest) {
        console.error('🔌 [HTTP] Request failed:', {
          url,
          status: response.status,
          statusText: response.statusText,
          error,
          timestamp: new Date().toISOString()
        })
      }

      // 憑證失效：清除本地狀態讓 AuthGuard 重新登入。
      // 排除 auth 端點本身（登入失敗不該觸發「登出」處理）。
      if (response.status === 401 && !endpoint.startsWith('/api/v1/auth/')) {
        void handleUnauthorized()
      }

      const retryAfterHeader = response.headers.get('Retry-After')
      const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) || undefined : undefined
      throw new ApiError(
        response.status,
        error.error || error.message || `HTTP ${response.status}`,
        retryAfterSeconds
      )
    }

    const responseData = await response.json()
    
    if (isComboRequest) {
      debugLog('🔌 [HTTP] Response received:', {
        url,
        status: response.status,
        statusText: response.statusText,
        data: responseData,
        timestamp: new Date().toISOString()
      })
    }

    return responseData
  }

  // Authentication
  async telegramLogin(initData: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/v1/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ telegramData: initData }),
    })
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    return this.request<RefreshTokenResponse>('/api/v1/auth/refresh', {
      method: 'POST',
    })
  }

  async logout(): Promise<{ message: string }> {
    return this.request('/api/v1/auth/logout', { method: 'POST' })
  }

  async devLogin(credentials: DevLoginInput): Promise<DevLoginResponse> {
    return this.request<DevLoginResponse>('/api/v1/auth/dev', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  // Courses
  async getCourses(params?: CourseQueryParams): Promise<ApiResponse<Course[]>> {
    const query = params ? `?${new URLSearchParams(params as any).toString()}` : ''
    return this.request<ApiResponse<Course[]>>(`/api/v1/courses${query}`)
  }

  async getCourse(id: string): Promise<Course> {
    return this.request<Course>(`/api/v1/courses/${id}`)
  }

  async createCourse(courseData: CreateCourseInput): Promise<Course> {
    return this.request<Course>('/api/v1/courses', {
      method: 'POST',
      body: JSON.stringify(courseData),
    })
  }

  async updateCourse(id: string, courseData: UpdateCourseInput): Promise<Course> {
    return this.request<Course>(`/api/v1/courses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(courseData),
    })
  }

  async updateCourseStatus(id: string, statusUpdate: { status: Course['status'] }): Promise<Course> {
    return this.request<Course>(`/api/v1/courses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(statusUpdate),
    })
  }

  async deleteCourse(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/v1/courses/${id}`, {
      method: 'DELETE',
    })
  }

  // Users
  async getProfile(): Promise<User> {
    return this.request<User>('/api/v1/users/profile')
  }

  async updateProfile(userData: UpdateUserInput): Promise<User> {
    return this.request<User>('/api/v1/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(userData),
    })
  }

  async getUsers(params?: UserQueryParams): Promise<ApiResponse<User[]>> {
    const queryParams = new URLSearchParams()

    if (params?.search) queryParams.append('search', params.search)
    if (params?.role) queryParams.append('role', params.role)
    if (params?.status) queryParams.append('status', params.status)
    if (typeof params?.isActive === 'boolean') queryParams.append('isActive', params.isActive.toString())
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const endpoint = queryString ? `/api/v1/users?${queryString}` : '/api/v1/users'

    const response = await this.request<ApiResponse<any[]>>(endpoint)

    return {
      ...response,
      data: mapUsersData(response.data)
    }
  }

  async getUser(id: string): Promise<User> {
    console.log(`[API] Getting user with ID: ${id}`)
    try {
      const rawUser = await this.request<any>(`/api/v1/users/${id}`)
      console.log(`[API] getUser raw response:`, rawUser)
      
      const mappedUser = mapUserData(rawUser)
      console.log(`[API] getUser mapped response:`, mappedUser)
      return mappedUser
    } catch (error) {
      console.error(`[API] getUser failed for ID ${id}:`, error)
      throw error
    }
  }

  async createUser(userData: CreateUserInput): Promise<User> {
    return this.request<User>('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async updateUser(id: string, userData: AdminUpdateUserInput): Promise<User> {
    const rawUser = await this.request<any>(`/api/v1/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(userData),
    })
    return mapUserData(rawUser)
  }

  async deleteUser(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/v1/users/${id}`, {
      method: 'DELETE',
    })
  }

  async createUserFromTelegram(userData: CreateUserFromTelegramInput): Promise<User> {
    return this.request<User>('/api/v1/users/telegram', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async checkUserByTelegramId(telegramId: number | string): Promise<{ exists: boolean; user?: User }> {
    return this.request<{ exists: boolean; user?: User }>(`/api/v1/users/telegram/${telegramId}`)
  }

  // Enrollment endpoints
  async getEnrollments(params?: EnrollmentQueryParams): Promise<ApiResponse<Enrollment[]>> {
    const queryParams = new URLSearchParams()
    
    if (params?.search) queryParams.append('search', params.search)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.studentId) queryParams.append('studentId', params.studentId)
    if (params?.teacherId) queryParams.append('teacherId', params.teacherId)
    if (params?.courseId) queryParams.append('courseId', params.courseId)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    
    const queryString = queryParams.toString()
    const endpoint = queryString ? `/api/v1/enrollments?${queryString}` : '/api/v1/enrollments'
    
    return this.request<ApiResponse<Enrollment[]>>(endpoint)
  }

  async getEnrollment(id: string): Promise<Enrollment> {
    return this.request<Enrollment>(`/api/v1/enrollments/${id}`)
  }

  async createEnrollment(enrollmentData: CreateEnrollmentInput): Promise<Enrollment> {
    return this.request<Enrollment>('/api/v1/enrollments', {
      method: 'POST',
      body: JSON.stringify(enrollmentData),
    })
  }

  async updateEnrollment(id: string, enrollmentData: UpdateEnrollmentInput): Promise<Enrollment> {
    return this.request<Enrollment>(`/api/v1/enrollments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(enrollmentData),
    })
  }

  async updateEnrollmentStatus(id: string, statusUpdate: { status: Enrollment['status'] }): Promise<Enrollment> {
    return this.request<Enrollment>(`/api/v1/enrollments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(statusUpdate),
    })
  }

  async deleteEnrollment(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/v1/enrollments/${id}`, {
      method: 'DELETE',
    })
  }

  // Payments (Stripe Checkout)
  async createCheckoutSession(enrollmentId: string): Promise<CheckoutSessionResponse> {
    return this.request<CheckoutSessionResponse>('/api/v1/payments/checkout', {
      method: 'POST',
      body: JSON.stringify({ enrollmentId }),
    })
  }

  async getPaymentStatus(enrollmentId: string): Promise<PaymentStatusResponse> {
    return this.request<PaymentStatusResponse>(`/api/v1/payments/status/${enrollmentId}`)
  }

  // Platform settings (admin)
  async getAppSettings(): Promise<AppSettings> {
    return this.request<AppSettings>('/api/v1/settings')
  }

  async updateAppSettings(settings: AppSettings): Promise<AppSettings> {
    return this.request<AppSettings>('/api/v1/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    })
  }

  // Client UI orchestration config
  async getClientUiConfig(): Promise<ClientUiConfig> {
    return this.request<ClientUiConfig>('/api/v1/settings/client-ui')
  }

  async updateClientUiConfig(config: ClientUiConfig): Promise<ClientUiConfig> {
    return this.request<ClientUiConfig>('/api/v1/settings/client-ui', {
      method: 'PATCH',
      body: JSON.stringify(config),
    })
  }

  // Dashboard statistics
  async getAdminStats(): Promise<AdminStats> {
    return this.request<AdminStats>('/api/v1/stats/admin')
  }

  async getTeacherStats(): Promise<TeacherStats> {
    return this.request<TeacherStats>('/api/v1/stats/teacher')
  }

  // Combos
  async getCombos(params?: ComboQueryParams): Promise<ApiResponse<Combo[]>> {
    const queryParams = new URLSearchParams()
    
    if (params?.search) queryParams.append('search', params.search)
    if (params?.teacherId) queryParams.append('teacherId', params.teacherId)
    if (params?.isActive !== undefined) queryParams.append('is_active', params.isActive.toString())
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    
    const queryString = queryParams.toString()
    const endpoint = queryString ? `/api/v1/combos?${queryString}` : '/api/v1/combos'
    
    // API 直接返回 { data: [], pagination: {} } 格式，不需要額外包裝
    return this.request<ApiResponse<Combo[]>>(endpoint)
  }

  async getCombo(id: string): Promise<Combo> {
    return this.request<Combo>(`/api/v1/combos/${id}`)
  }

  async createCombo(comboData: CreateComboInput & { teacherId: string }): Promise<Combo> {
    debugLog('🌐 [API] Creating combo - Request data:', {
      endpoint: '/api/v1/combos',
      method: 'POST',
      payload: { ...comboData },
      timestamp: new Date().toISOString()
    })
    
    const result = await this.request<Combo>('/api/v1/combos', {
      method: 'POST',
      body: JSON.stringify(comboData),
    })
    
    debugLog('🌐 [API] Create combo - Response:', {
      success: true,
      result,
      timestamp: new Date().toISOString()
    })
    
    return result
  }

  async updateCombo(id: string, comboData: UpdateComboInput): Promise<Combo> {
    debugLog('🌐 [API] Updating combo - Request data:', {
      endpoint: `/api/v1/combos/${id}`,
      method: 'PATCH',
      comboId: id,
      payload: { ...comboData },
      timestamp: new Date().toISOString()
    })
    
    const result = await this.request<Combo>(`/api/v1/combos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(comboData),
    })
    
    debugLog('🌐 [API] Update combo - Response:', {
      success: true,
      comboId: id,
      result,
      timestamp: new Date().toISOString()
    })
    
    return result
  }

  async deleteCombo(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/v1/combos/${id}`, {
      method: 'DELETE',
    })
  }

  // File upload
  async uploadFile(file: File): Promise<{ url: string }> {
    const formData = new FormData()
    formData.append('file', file)
    
    const token = typeof window !== 'undefined' 
      ? localStorage.getItem('access_token') 
      : null
    
    const response = await fetch(`${this.baseURL}/api/v1/upload`, {
      method: 'POST',
      body: formData,
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: 'Upload failed',
      }))
      throw new Error(error.error || 'Upload failed')
    }

    return response.json()
  }
}

// Create API client that switches between mock and real API
const createApiClient = () => {
  if (apiModeManager.shouldUseMock()) {
    console.log('Using Mock API for development')
    return mockApiClient
  }
  console.log('Using Database API:', API_BASE_URL)
  return new ApiClient(API_BASE_URL)
}

// 初始化 API 客戶端
let currentApiClient = createApiClient()

// 監聽模式變更並重新創建客戶端
apiModeManager.addListener(() => {
  currentApiClient = createApiClient()
  console.log('API Client recreated due to mode change')
})

// 導出 API 客戶端的 Proxy，自動使用最新的客戶端
export const apiClient = new Proxy({} as any, {
  get(_, prop) {
    return currentApiClient[prop as keyof typeof currentApiClient]
  }
})